import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import PlanImpact from '../../PlanImpact/PlanImpactContainer';
import AdvForm from '../../../CommonComponents/AdvForm';
import Footer from '../../Footer/FooterContainer';
import Loading from '../../Loading/LoadingView';
import {
  isNumeric,
  isUuid,
  createCustomPropType,
  isNumericFormatted,
} from '../../../../js/app/react/validators';
import ValidationErrors from '../../ValidationErrors/ValidationErrorsContainer';
import AdvInput, { advInputTextChanged } from '../../../CommonComponents/AdvInput';
import AdvInputWrapper from '../../../CommonComponents/AdvInputWrapper';
import Tooltip from '../../../CommonComponents/Tooltip';

import {
  CHARITABLE_GIVING_DONATION_AMOUNT,
  LARGE_PURCHASE_GENERAL_PRICE_CHANGES,
} from '../../WhatIfsConstants';
import { MAXIMIZE_DONATIONS_TOOLTIP_CONTENT } from '../../../CharitableGivingWorkflow/stages/CharitableGiving/steps/DonationAmount/DonationAmount.meta';
import { GivingStrategy } from '../../../CharitableGivingWorkflow/stages/CharitableGiving/api/useCharitableGivingQuery';

export const FIELD_MAP = {
  [LARGE_PURCHASE_GENERAL_PRICE_CHANGES]: 'Price',
  [CHARITABLE_GIVING_DONATION_AMOUNT]: 'DonationAmount',
};

class PriceChangesChangesView extends Component {
  constructor() {
    super();
    this.onPriceBlur = this.onPriceBlur.bind(this);
  }
  componentDidMount() {
    const {
      fetchData,
      clientId,
      fpId,
      goalId,
      fetchInitialPlanImpact,
      whatIf,
      whatIfTemplate,
    } = this.props;
    fetchData(clientId, fpId, goalId, whatIfTemplate.WhatIfType);
    fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  }

  componentDidUpdate(previousProps) {
    const { clientId, fpId, goalId, whatIf, fetchPlanImpact, whatIfTemplate } = this.props;

    const impactTriggerProps = ['whatIf'];

    if (!isEqual(pick(this.props, impactTriggerProps), pick(previousProps, impactTriggerProps))) {
      fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }

  onPriceBlur(valueArg) {
    const { goalId, clientId, whatIfTemplate, storeData } = this.props;
    const fieldName = FIELD_MAP[whatIfTemplate.WhatIfType];
    let value = valueArg;
    if (isNumericFormatted(valueArg)) {
      if (typeof valueArg === 'string') {
        value = +valueArg.replace(/[,]/g, '');
      }
    } else {
      value = 0;
    }
    advInputTextChanged(
      clientId,
      storeData(goalId, whatIfTemplate.WhatIfType),
      `WhatIf.${fieldName}`,
    )(value);
  }

  render() {
    const {
      goalId,
      clientId,
      loading,
      title,
      groupTitle,
      fpId,
      whatIf,
      whatIfTemplate,
      applyWhatIf,
      areOptionsLoaded,
      planImpactItems,
      purchasePrice,
      storeData,
      givingStrategy,
    } = this.props;
    const isDonationAmountQCD = givingStrategy === GivingStrategy.QualifiedCharitableDonations;
    const maximizeDonations = !!(whatIf && whatIf.MaximizeDonations);
    const { ADV } = window;

    const inputValue =
      isDonationAmountQCD && maximizeDonations ? '' : ADV.util.formatValue(purchasePrice, 'money');

    return (
      <div className="what-ifs-layout">
        {loading && <Loading />}
        <div className="what-ifs-section what-ifs-layout-header">
          <h3>What-If...</h3>
        </div>
        <div className="what-ifs-layout-content">
          <h4 className="what-if-group-title">{groupTitle}</h4>
          <AdvForm>
            <ValidationErrors clientId={clientId} />
            <ul className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}>
              <li className="what-ifs-form-field">
                <h5 className="what-if-title">{title}</h5>
              </li>
              <li className="what-ifs-large-purchase-price-form-field">
                <AdvInputWrapper
                  hasCurrency
                  className={`what-ifs-input-wrapper${
                    isDonationAmountQCD && maximizeDonations ? ' disabled' : ''
                  }`}
                >
                  <AdvInput
                    autoComplete="off"
                    type="text"
                    dataType="money"
                    maxLength="14"
                    onBlur={this.onPriceBlur}
                    value={inputValue}
                    className="what-ifs-input"
                    blankable={isDonationAmountQCD && maximizeDonations}
                    disabled={isDonationAmountQCD && maximizeDonations}
                  />
                </AdvInputWrapper>
              </li>
              {isDonationAmountQCD && (
                <li className="what-ifs-form-field what-ifs-form-field-no-box">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label
                      htmlFor="MaximizeDonations"
                      className="what-ifs-age-label what-ifs-checkbox"
                      style={{ marginRight: '8px' }}
                    >
                      <input
                        type="checkbox"
                        id="MaximizeDonations"
                        className="what-ifs-checkbox-toggle"
                        checked={maximizeDonations}
                        onChange={event => {
                          const checked = event.target.checked;
                          const fieldName = FIELD_MAP[whatIfTemplate.WhatIfType];
                          const whatIfPayload = {
                            MaximizeDonations: checked,
                          };

                          if (checked && fieldName) {
                            whatIfPayload[fieldName] = null;
                          }

                          storeData(goalId, whatIfTemplate.WhatIfType)(
                            {
                              WhatIf: whatIfPayload,
                            },
                            clientId,
                          );
                        }}
                      />
                      <i className="what-ifs-checkbox-mark" />
                      <span className="what-ifs-checkbox-label-text">Maximize Donations</span>
                      <Tooltip
                        tag="i"
                        className="far fa-info-circle"
                        style={{ marginLeft: '8px' }}
                        tooltipchildren={MAXIMIZE_DONATIONS_TOOLTIP_CONTENT}
                        orientation="left"
                      />
                    </label>
                  </div>
                </li>
              )}
            </ul>
          </AdvForm>
        </div>
        <PlanImpact clientId={clientId} planImpactItems={planImpactItems} />
        <Footer
          clientId={clientId}
          applyToPlanCallback={applyWhatIf(clientId, fpId, goalId, whatIf, whatIfTemplate)}
        />
      </div>
    );
  }
}
PriceChangesChangesView.defaultProps = {
  title: 'Untitled What If',
  groupTitle: 'Untitled What If Group',
};

PriceChangesChangesView.propTypes = {
  planImpactItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      current: PropTypes.string.isRequired,
      proposed: PropTypes.string.isRequired,
    }),
  ).isRequired,
  goalId: createCustomPropType(isUuid).isRequired,
  fpId: createCustomPropType(isUuid).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  purchasePrice: createCustomPropType(isNumericFormatted).isRequired,
  title: PropTypes.string,
  groupTitle: PropTypes.string,
  areOptionsLoaded: PropTypes.bool.isRequired,
  whatIf: PropTypes.shape({}).isRequired,
  fetchPlanImpact: PropTypes.func.isRequired,
  whatIfTemplate: PropTypes.shape({}).isRequired,
  fetchData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fetchInitialPlanImpact: PropTypes.func.isRequired,
  applyWhatIf: PropTypes.func.isRequired,
  storeData: PropTypes.func.isRequired,
  givingStrategy: PropTypes.number,
};

export default PriceChangesChangesView;
